import { useEffect, useState } from "react";
import {
  Button,
  Typography,
  Box,
  LinearProgress,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Upload, CheckCircle, Download } from "@mui/icons-material";
import { processTranslator } from "../../../lib/api_routes/aiToolkit";
import { TOOL_TYPES } from "../../../utils/aiToolkitInfo";
import { InstructionSection } from "../../AiToolkit/InstructionSection";
import { useAccountInfo } from "../../../hooks/useAccountInfo";
import { useActivityTracking } from "../../../hooks/userActivityTracking";
import { formatAxiosError } from "../../../utils/error_message";
import theme from "../../../theme";

const useFileUpload = (accountId) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [processedBlobUrl, setProcessedBlobUrl] = useState(null);
  const [processComplete, setProcessComplete] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  // USER ACTIVITY TRACKING
  const { trackAction, resetSession } = useActivityTracking(
    "ai_toolkit_translator",
    accountId,
  );

  // Update steps based on file upload
  useEffect(() => {
    if (uploadComplete && !completedSteps.has(1)) {
      setCompletedSteps((prev) => new Set([...prev, 1]));
      setCurrentStep(2);
    }
  }, [uploadComplete, completedSteps]);

  // Update steps based on processing completition
  useEffect(() => {
    if (processComplete && !completedSteps.has(2)) {
      setCompletedSteps((prev) => new Set([...prev, 2]));
      setCurrentStep(3);
    }
  }, [processComplete, completedSteps]);

  // Complete the final step
  const handleCompletedSteps = async () => {
    await handleDownload();
    setCompletedSteps((prev) => new Set([...prev, 3]));
  };

  // Reset all states to go back to step 1
  const goBackToStep1 = () => {
    setFile(null);
    setUploadComplete(false);
    setProgress(0);
    setCurrentStep(1);
    setCompletedSteps(new Set());
    setProcessedBlobUrl(null);
    setProcessComplete(false);
    resetSession();

    // Reset file input
    const fileInput = document.getElementById("file-upload");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  // Process the uploaded file with backend
  const processFile = async () => {
    if (!file) return;
    setLoading(true);

    try {
      const response = await processTranslator(file);

      // Create BlobURL for final processing
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      setProcessedBlobUrl(url);
      setProcessComplete(true);

      // USER ACTIVITY TRACKING - Track success
      await trackAction("Process Translator", "SUCCESS", null, {
        fileName: file?.name,
      });
    } catch (error) {
      const { serverMessage, fullError } = await formatAxiosError(error);
      alert(serverMessage ?? "Failed to process file.");
      // USER ACTIVITY TRACKING - Track failure and reset so retry starts fresh
      await trackAction("Process Translator", "FAILED", fullError, {
        fileName: file?.name,
      }).catch(console.error);
    } finally {
      setLoading(false);
    }
  };

  // Download processed file
  const handleDownload = async () => {
    if (!processedBlobUrl || !file) return;

    const downloadFileName = "Processed_translation_output.xlsx";

    const link = document.createElement("a");
    link.href = processedBlobUrl;
    link.setAttribute("download", downloadFileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // USER ACTIVITY TRACKING - append download processed
    trackAction("Download Processed File", "SUCCESS", null, {
      fileName: file?.name,
      downloadFileName,
    }).catch(console.error);
  };

  const handleFileUpload = (event) => {
    const ALLOWED_EXTENSIONS = ["xlsx"];
    const UPLOAD_PROGRESS_INTERVAL = 200;
    const UPLOAD_PROGRESS_STEP = 20;

    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    const fileExtension = uploadedFile.name.split(".").pop().toLowerCase();

    if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
      alert("Only .xlsx files are allowed!");
      return;
    }

    // Reset all states for new upload
    setFile(uploadedFile);
    setUploadComplete(false);
    setProcessComplete(false);
    setProgress(0);
    setProcessedBlobUrl(null);

    // Simulate progress bar
    setLoading(true);
    let progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          setUploadComplete(true);
          setLoading(false);
          return 100;
        }
        return prev + UPLOAD_PROGRESS_STEP;
      });
    }, UPLOAD_PROGRESS_INTERVAL);
  };

  return {
    file,
    loading,
    progress,
    uploadComplete,
    processComplete,
    currentStep,
    completedSteps,
    handleFileUpload,
    processFile,
    handleDownload,
    handleCompletedSteps,
    goBackToStep1,
  };
};

const FileProgressIndicator = ({
  fileName,
  progress,
  uploadComplete,
  disabled,
}) => {
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
        {fileName}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: theme.palette.gray.medium,
          "& .MuiLinearProgress-bar": {
            backgroundColor: disabled
              ? theme.palette.gray.medium
              : uploadComplete
                ? theme.palette.secondary.lawnGreen
                : theme.palette.primary.main,
          },
        }}
      />
    </Box>
  );
};

const FileUploadForm = ({ handleFileUpload, uploadComplete, disabled }) => {
  return (
    <>
      <input
        type="file"
        id="file-upload"
        style={{ display: "none" }}
        onChange={handleFileUpload}
        accept=".xlsx"
      />
      <label htmlFor="file-upload">
        <Button
          component="span"
          variant="contained"
          fullWidth
          startIcon={uploadComplete ? <CheckCircle /> : <Upload />}
          sx={{
            backgroundColor: disabled
              ? theme.palette.gray.dark
              : theme.palette.primary.main,
            "&:hover": {
              backgroundColor: theme.palette.primary.main,
            },
            mb: 1,
          }}
        >
          {uploadComplete ? "Upload Complete" : "Upload File"}
        </Button>
      </label>
    </>
  );
};

const FileProcessing = ({ disabled, onProcess, processLoading, onGoBack }) => {
  return (
    <Box
      sx={{
        my: 2,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Process Button */}
        <Button
          disabled={disabled}
          onClick={onProcess}
          variant="contained"
          sx={{
            width: "100%",
            backgroundColor: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
              color: theme.palette.primary.white,
            },
            "&:disabled": {
              backgroundColor: theme.palette.gray.dark,
              color: theme.palette.primary.white,
              border: "none",
            },
          }}
          startIcon={
            processLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : null
          }
        >
          {processLoading ? "Processing..." : "Process File"}
        </Button>

        {/* Go back Button - if user want to change the download file */}
        <Button
          disabled={disabled}
          onClick={onGoBack}
          variant="contained"
          sx={{
            width: "100%",
            backgroundColor: theme.palette.primary.white,
            border: `1px solid ${theme.palette.primary.main}`,
            color: theme.palette.primary.main,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: theme.palette.primary.medium,
              color: theme.palette.primary.brandSecondary,
              border: `1px solid ${theme.palette.primary.brandSecondary}`,
            },
            "&:disabled": {
              backgroundColor: theme.palette.gray.medium,
              border: "none",
            },
          }}
        >
          Go Back
        </Button>
      </Box>
    </Box>
  );
};

const ProcessingCompletedScreen = ({ handleDownload }) => {
  return (
    <Box sx={{ mt: 3 }}>
      <Alert
        severity="success"
        sx={{ mb: 2, alignItems: "center" }}
        icon={<CheckCircle />}
      >
        <Typography variant="subtitle1" component="div">
          File Processed Successfully!
        </Typography>
        <Typography variant="body2">
          Your file has been processed and is ready for download.
        </Typography>
      </Alert>
      <Button
        onClick={handleDownload}
        variant="contained"
        startIcon={<Download />}
        sx={{
          backgroundColor: theme.palette.primary.main,
          "&:hover": { backgroundColor: theme.palette.primary.main },
        }}
        fullWidth
      >
        Download Processed File
      </Button>
    </Box>
  );
};

export function TranslatorForm() {
  // Account Info from hooks
  const { accountInfo } = useAccountInfo();
  const {
    file,
    loading,
    progress,
    uploadComplete,
    currentStep,
    completedSteps,
    processComplete,
    handleCompletedSteps,
    handleFileUpload,
    processFile,
    goBackToStep1,
  } = useFileUpload(accountInfo?.accountId);

  return (
    <>
      <Typography variant="h4" sx={{ mb: 4, py: 2, textAlign: "center" }}>
        Translation Process
      </Typography>
      <InstructionSection
        currentStep={currentStep}
        completedSteps={completedSteps}
        toolType={TOOL_TYPES.TRANSLATOR}
      />
      <Box
        sx={{
          m: "auto",
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr",
            md: "repeat(3, 1fr)",
          },
          alignItems: "stretch",
          justifyContent: "center",
          margin: "auto",
          gap: 2,
          p: 2,
          my: 4,
          maxWidth: {
            xs: "400px",
            sm: "420px",
            md: "75rem",
          },
        }}
      >
        {/* Step 1: File Upload Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            p: 2,
            height: "100%",
            backgroundColor: theme.palette.primary.lightBackground,
            borderRadius: 2,
            opacity: currentStep === 1 ? 1 : completedSteps.has(1) ? 0.6 : 0.4,
            pointerEvents: currentStep === 1 ? "auto" : "none",
            order: { xs: 1, md: 1 },
          }}
        >
          <Typography variant="h6" sx={{ textAlign: "center" }}>
            Upload Your Excel File
          </Typography>
          <FileUploadForm
            handleFileUpload={handleFileUpload}
            disabled={currentStep !== 1}
          />
          {file && (
            <FileProgressIndicator
              fileName={file.name}
              progress={progress}
              uploadComplete={uploadComplete}
              disabled={currentStep !== 1}
            />
          )}
        </Box>

        {/* Step 2: Testing/Processing Section */}
        {uploadComplete && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              p: 2,
              height: "100%",
              backgroundColor: theme.palette.primary.lightBackground,
              borderRadius: 2,
              opacity:
                currentStep === 2 ? 1 : completedSteps.has(2) ? 0.6 : 0.4,
              pointerEvents: currentStep === 2 ? "auto" : "none",
              order: { xs: 2, md: 2 },
            }}
          >
            <Typography variant="h6" sx={{ textAlign: "center" }}>
              Process File
            </Typography>
            <FileProcessing
              disabled={currentStep !== 2 || !file}
              onProcess={processFile}
              processLoading={loading}
              onGoBack={goBackToStep1}
            />
          </Box>
        )}

        {/* Step 3: Download Results Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            p: 2,
            height: "100%",
            backgroundColor: theme.palette.primary.lightBackground,
            borderRadius: 2,
            opacity: currentStep === 3 ? 1 : 0.4,
            pointerEvents: currentStep === 3 ? "auto" : "none",
            order: { xs: 3, md: 3 },
          }}
        >
          {processComplete && (
            <>
              <Typography variant="h6" sx={{ textAlign: "center" }}>
                Download Results
              </Typography>
              <ProcessingCompletedScreen
                handleDownload={handleCompletedSteps}
                disabled={currentStep !== 3}
              />
            </>
          )}
        </Box>
      </Box>
    </>
  );
}
